import type { ReactNode } from 'react'

import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { Text, VStack } from '@enechain/ecloud-designsystem'

import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'

type RejectionRow = { key: string; text: string }

// react/no-unstable-nested-components 回避のため、 列定義は component 本体の外に置く。
function buildColumns(
  t: TFunction,
  errorBadge: ReactNode,
): Array<MasterDataTableColumn<RejectionRow>> {
  return [
    {
      key: 'status',
      header: t('page:biddingCommon.csvPreview.columns.status'),
      cell: () => errorBadge,
    },
    {
      key: 'message',
      header: t('page:biddingCommon.csvPreview.columns.message'),
      cell: (row) => row.text,
    },
  ]
}

type Props = {
  /** 行番号つきのエラー文言 (「行 N: …」の全文。 backend が返した順)。 */
  rowMessages: string[]
  /** 見出しに出す、 エラーのある行の数。 */
  errorRowCount: number
  /** ファイル全体が理由の文言 (行番号なし)。 */
  fileReasons: string[]
  /** エラー行のステータスのバッジ。 その画面の Step2 のエラー行と同じものを渡す。 */
  errorBadge: ReactNode
  /**
   * 行エラーがすべて、 時間をおくと解消する原因 (開場前・基準価格の値が無い 等) の時、 true。
   * 「CSV を直して取り込み直してください。」の代わりに「時間をおいてから、 取り込み直して
   * ください。」を案内する (案内は原因ごとに 1 つ)。 CSV の誤りが 1 行でもあれば false。
   */
  retryLater?: boolean
}

/**
 * 入札 CSV の確定時の再検証で取込全体が拒否されたときの、 確定モーダルのエラー表示
 * (Des 回答 #8786)。 スポット通常入札と時間前入札で共有する。 拒否理由の取り出し方
 * (文言の解析 / error detail) は種別ごとに違うので、 呼び出し側が分けてから渡す。
 *
 * 行番号つきのエラーを全件、 Step2 のエラー行と同じ部品 (`MasterDataTable` +
 * 行ステータスのバッジ + 同じ列見出し) で出す。 違うのはスコープだけ (Step2 = その行が
 * 対象外 / 確定時 = 取込全体が拒否) なので見出しで分ける。 ファイル全体が理由
 * (行番号なし) の場合は行リストの前に 1 行で出す。
 *
 * 「CSV を直して取り込み直してください。」は行エラーがある時だけ出す。 案内は原因ごとに
 * 1 つ (Des 回答 #8786 の判断 2) で、 ファイル全体が理由の文言 (手数料単価の変更など) は
 * backend が次の操作まで含めて返すため、 重ねると案内が 2 つ並ぶ。 行エラーがすべて時間を
 * おくと解消する原因の時は (`retryLater`)、 CSV を直す案内ではなく「時間をおいてから、
 * 取り込み直してください。」を出す (spec intraday-csv-import.md §7)。
 */
export const BidCsvConfirmRejection = ({
  rowMessages,
  errorRowCount,
  fileReasons,
  errorBadge,
  retryLater = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const rows = rowMessages.map((text, index) => ({
    key: `${index}:${text}`,
    text,
  }))

  return (
    <VStack align="stretch" gap={3}>
      <Text textStyle="label">
        {rows.length > 0
          ? t('page:biddingCommon.csvConfirmRejection.title', {
              count: errorRowCount,
            })
          : t('page:biddingCommon.csvConfirmRejection.titleNoRows')}
      </Text>
      {fileReasons.map((reason) => (
        <Text key={reason}>{reason}</Text>
      ))}
      {rows.length > 0 && (
        <MasterDataTable
          columns={buildColumns(t, errorBadge)}
          getRowKey={(row) => row.key}
          rows={rows}
        />
      )}
      {rows.length > 0 && (
        <Text>
          {t(
            retryLater
              ? 'page:biddingCommon.csvConfirmRejection.retryLater'
              : 'page:biddingCommon.csvConfirmRejection.fixCsv',
          )}
        </Text>
      )}
      <Text color="Text.midEmphasis" fontSize="sm">
        {t('page:biddingCommon.csvConfirmRejection.historyNote')}
      </Text>
    </VStack>
  )
}
